/**
 * 業務フローの図とステージの図の ⓘ。見出しと図の枠に 1 つずつ置き、補足を指すと出す。
 *
 * **図の上に補足の文を並べない**（Dhik 2026-09-29「テキスト量が多いことが課題」）。状況・入口・
 * 出口・操作の案内・線の描き方の凡例は、知らないと読み違えるが毎回は読まない。見出しと図の枠に
 * ⓘ を 1 つずつ置き、中身をそこへ束ねる（1 画面 3 つまで）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの図・ステージの図の見出しと図の枠に、補足の文を指す・フォーカスで出す ⓘ を描く。
 * @graph-connects flowFrameInfoLines [calls] 図の枠の ⓘ に束ねる行
 */

/** 宛先の描き方の凡例の並び。 @graph-connects none */
const LEGEND_KINDS = ['next', 'back', 'stop', 'exit', 'entry'] as const;

/**
 * 図の枠の ⓘ に束ねる行 — 操作の案内・宛先の描き方の凡例・案件モードの色の凡例。
 *
 * @param t 訳
 * @param pjMode 案件から見た図か（追加 ／ 削除の色の凡例を足す）
 * @returns ⓘ に出す行
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの図の枠の ⓘ に束ねる、操作の案内・宛先の描き方の凡例・案件の色の凡例を行にする。
 * @graph-connects none
 */
export function flowFrameInfoLines(t: (key: string) => string, pjMode: boolean): string[] {
  return [
    t('flow.frameHint'),
    `${t('flow.legendTitle')}:`,
    ...LEGEND_KINDS.map((kind) => `・${t(`flow.legend.${kind}`)}`),
    ...(pjMode ? [t('pjFlow.legendDeleted'), t('pjFlow.legendAdded')] : []),
  ];
}

/**
 * ⓘ 1 つ。
 *
 * 中身は `title`（指すと出る）と `aria-label`（読み上げ）に同じ文を入れる。`<button>` にするのは、
 * キーボードで辿れて、支援技術にも「フォーカスして読めるもの」として伝わるためである。見えている
 * 「i」は飾りなので読み上げから外す。行は改行でつなぐ（`title` は改行をそのまま出す）。
 *
 * @param props 出す文の行
 * @returns ⓘ のボタン。行が 1 つも無ければ何も描かない
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 補足の文の行を、指す・フォーカスで出る ⓘ のボタンとして描き、行が無ければ描かない。
 * @graph-connects none
 */
export function FlowInfoMark({ lines }: { lines: readonly string[] }) {
  if (lines.length === 0) return null;
  const text = lines.join('\n');
  return (
    <button
      type="button"
      title={text}
      aria-label={text}
      data-info-mark
      className="inline-grid h-4 w-4 shrink-0 cursor-help place-items-center rounded-full border border-[var(--color-text-muted)] text-[10px] leading-none font-bold text-[var(--color-text-muted)] focus-visible:outline-2 focus-visible:outline-[var(--color-border-focus)]"
    >
      <span aria-hidden="true">i</span>
    </button>
  );
}
